
import React from 'react';
import Layout from '@/components/Layout/Layout';
import EBITDACalculator from '@/components/SpecialCalculators/FinancialCalculators/EBITDACalculator';
import { Link } from 'react-router-dom';

const EBITDACalculatorPage = () => {
  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4">
        {/* Header Section */}
        <div className="mb-8">
          <div className="text-sm text-gray-500 mb-2">
            <Link to="/" className="hover:underline">Home</Link> {' > '} 
            <Link to="/special" className="hover:underline">Special Tools</Link> {' > '} 
            <span>EBITDA Calculator</span>
          </div>
          <h1 className="text-3xl font-bold mb-3">EBITDA Calculator</h1>
          <p className="text-gray-700">
            Calculate Earnings Before Interest, Taxes, Depreciation, and Amortization (EBITDA) to measure your company's operating performance and profitability.
          </p>
        </div>

        {/* Calculator Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <EBITDACalculator />
        </div>

        {/* Information Section */}
        <div className="bg-[#E6F3FF] p-6 rounded-lg mb-8">
          <h2 className="text-2xl font-semibold mb-3">How to use the EBITDA Calculator</h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-2">
            <li>Enter your company's total revenue for the period</li>
            <li>Input the cost of goods sold (COGS)</li>
            <li>Add your operating expenses (excluding depreciation and amortization)</li>
            <li>The calculator will automatically compute your EBITDA and EBITDA margin</li>
            <li>Compare your results with industry benchmarks for performance evaluation</li>
          </ol>
        </div>

        {/* Additional Information */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Understanding EBITDA</h2>
          <p className="text-gray-700 mb-3">
            EBITDA (Earnings Before Interest, Taxes, Depreciation, and Amortization) is a financial metric that measures 
            a company's operating performance by excluding non-operating expenses and non-cash charges. It provides a 
            clearer view of operational efficiency and cash generation capability.
          </p>
          <p className="text-gray-700 mb-3">
            EBITDA is particularly useful for comparing companies within the same industry, as it eliminates the effects 
            of financing decisions, accounting policies, and tax environments. Investors and analysts use EBITDA to 
            evaluate company performance, determine valuation multiples, and assess debt servicing capacity.
          </p>
          <h3 className="text-xl font-medium mb-2">Why EBITDA Matters</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
            <li><strong>Operating Performance:</strong> Shows how well the core business generates cash</li>
            <li><strong>Company Comparison:</strong> Enables fair comparison between companies with different capital structures</li>
            <li><strong>Valuation Tool:</strong> Used in enterprise value calculations and acquisition analysis</li>
            <li><strong>Cash Flow Proxy:</strong> Approximates cash available for debt service and capital investments</li>
          </ul>
        </div>

        {/* Related Calculators */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Related Financial Calculators</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/special/profit-margin" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">📊</span> Profit Margin Calculator
            </Link>
            <Link to="/special/gross-margin" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">💹</span> Gross Margin Calculator
            </Link>
            <Link to="/special/roi" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">💰</span> ROI Calculator
            </Link>
            <Link to="/special/npv" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">📈</span> NPV Calculator
            </Link>
            <Link to="/special/net-worth" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">💎</span> Net Worth Calculator
            </Link>
            <Link to="/special/investment" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">📊</span> Investment Calculator
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EBITDACalculatorPage;
